import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroCard from "@/components/HeroCard";
import NewsCard from "@/components/NewsCard";
import PopuIABlock from "@/components/PopuIABlock";
import ShortsBlock from "@/components/ShortsBlock";
import SocialBlock from "@/components/SocialBlock";
import NewsletterCTA from "@/components/NewsletterCTA";
import LatestNewsList from "@/components/LatestNewsList";
import RecommendedList from "@/components/RecommendedList";
import LiveBlock from "@/components/LiveBlock";
import BreakingNewsBanner from "@/components/BreakingNewsBanner";
import CountdownBanner from "@/components/CountdownBanner";
import AdSlot from "@/components/AdSlot";
import HomeWorldCupFixture from "@/components/HomeWorldCupFixture";
import HomeWorldCupResults from "@/components/HomeWorldCupResults";
import HomeFlightsWidget from "@/components/HomeFlightsWidget";
import MostReadWidget from "@/components/MostReadWidget";
import HomeTransitability from "@/components/HomeTransitability";
import HomeOpinion from "@/components/HomeOpinion";
import HomePolls from "@/components/HomePolls";
import HomePollsResults from "@/components/HomePollsResults";
import { api } from "@/lib/api";

export default function Home() {
  const [hero, setHero] = useState(null);
  const [breaking, setBreaking] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [latest, setLatest] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [layout, setLayout] = useState({
    sections: ["breaking", "live", "hero", "ia", "featured", "shorts_social", "latest_recommended"],
    live_visible: true,
    breaking_visible: true,
    live_position: "top",
  });

  useEffect(() => {
    api.get("/articles/hero").then((r) => setHero(r.data)).catch(() => {});
    api.get("/articles/breaking").then((r) => setBreaking(r.data)).catch(() => {});
    api.get("/home/layout").then((r) => setLayout(r.data)).catch(() => {});
    api.get("/articles", { params: { highlight: "featured", limit: 4 } }).then((r) => setFeatured(r.data.items || []));
    api.get("/articles", { params: { is_short: true, limit: 3 } }).then((r) => setShorts(r.data.items || []));
    api.get("/articles", { params: { is_short: false, limit: 6 } }).then((r) => setLatest((r.data.items || []).slice(0, 6)));
    api.get("/articles", { params: { is_short: false, limit: 10 } }).then((r) => {
      const items = (r.data.items || []).filter((a) => !["actualidad"].includes(a.category_slug)).slice(0, 4);
      setRecommended(items);
    });
  }, []);

  const sections = layout.sections || ["breaking", "live", "hero", "ia", "featured", "shorts_social", "latest_recommended"];
  const showBreaking = layout.breaking_visible && breaking;
  const showLive = layout.live_visible;

  const renderSection = (key) => {
    switch (key) {
      case "breaking":
        return showBreaking ? <BreakingNewsBanner article={breaking} /> : null;
      case "live":
        return showLive ? <LiveBlock /> : null;
      case "hero":
        return <HeroCard article={hero} />;
      case "ia":
        return <PopuIABlock />;
      case "featured":
        return featured.length > 0 ? (
          <section data-testid="featured-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 stagger">
            {featured.map((a) => <NewsCard key={a.id} article={a} />)}
          </section>
        ) : null;
      case "shorts_social":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            <div className="lg:col-span-3"><ShortsBlock shorts={shorts} /></div>
            <div className="lg:col-span-2"><SocialBlock /></div>
          </div>
        );
      case "latest_recommended":
        // Latest news extended with newsletter CTA on the right (recommended removed per request)
        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-9"><LatestNewsList articles={latest} /></div>
            <div className="lg:col-span-3"><NewsletterCTA /></div>
          </div>
        );
      case "latest_news":
        return (
          <section data-testid="latest-news-section">
            <LatestNewsList articles={latest} />
          </section>
        );
      case "recommended_news":
        return (
          <section data-testid="recommended-news-section">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2"><RecommendedList articles={recommended} /></div>
              <div className="md:col-span-1"><NewsletterCTA /></div>
            </div>
          </section>
        );
      case "newsletter":
        return (
          <section data-testid="newsletter-section" className="max-w-md mx-auto">
            <NewsletterCTA />
          </section>
        );
      case "countdown":
        return <CountdownBanner />;
      case "wc_fixture":
        return <HomeWorldCupFixture compact />;
      case "wc_results":
        return <HomeWorldCupResults />;
      case "flights":
        return <HomeFlightsWidget compact />;
      case "transitabilidad":
        return <HomeTransitability />;
      case "opinion":
        return <HomeOpinion />;
      case "polls":
        return <HomePolls />;
      case "polls_results":
        return <HomePollsResults />;
      default:
        return null;
    }
  };

  const SIDEBAR_KEYS = ["wc_fixture", "flights"];
  const hasSidebar = true; // Always keep a right sidebar on home for Most Read widget

  return (
    <>
      <Header />
      <main className="max-w-[1440px] mx-auto px-3 md:px-8 py-3 md:py-6">
        {(() => {
          const renderedAll = sections
            .map((k) => ({ k, node: renderSection(k) }))
            .filter((x) => x.node);
          const mainItems = renderedAll.filter((x) => !SIDEBAR_KEYS.includes(x.k));
          const sidebarItems = renderedAll.filter((x) => SIDEBAR_KEYS.includes(x.k));

          const renderMain = () => {
            const nodes = [];
            mainItems.forEach(({ k, node }, idx) => {
              const prevKey = idx > 0 ? mainItems[idx - 1].k : null;
              const tight = k === "countdown" || prevKey === "countdown";
              const gapClass = idx === 0
                ? ""
                : tight
                  ? "mt-3 md:mt-4"
                  : "mt-5 md:mt-8";
              nodes.push(
                <div key={k} className={gapClass}>
                  {node}
                </div>
              );
              // Inject ad slot between sections (not after countdown or breaking)
              if (idx === 1) {
                nodes.push(<AdSlot key="ad-top-strip" slot="home_header_strip" className="mt-4" />);
              }
              if (k === "latest_news") {
                nodes.push(<AdSlot key="ad-mid" slot="home_between_sections" className="mt-6" />);
              }
            });
            // Footer sponsor strip at the end
            nodes.push(<AdSlot key="ad-footer-strip" slot="home_footer_strip" className="mt-8" />);
            return nodes;
          };

          if (!hasSidebar) {
            return renderMain();
          }

          // Layout with right sidebar for wc_fixture
          return (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
              <div className="lg:col-span-9 min-w-0">
                {renderMain()}
              </div>
              <aside className="lg:col-span-3 min-w-0">
                <div className="space-y-4">
                  {sidebarItems.map(({ k, node }) => (
                    <div key={k}>
                      {node}
                      {k === "flights" && <div className="mt-4"><MostReadWidget /></div>}
                    </div>
                  ))}
                  {!sidebarItems.some((x) => x.k === "flights") && <MostReadWidget />}
                  <AdSlot slot="home_sidebar" />
                </div>
              </aside>
            </div>
          );
        })()}
      </main>
      <Footer />
    </>
  );
}
